<template>
  <v-card flat class="filters-sidebar" elevation="3">
    <!-- Header -->
    <div class="filters-header">
      <v-icon color="white" size="20">mdi-filter-variant</v-icon>
      <span class="filters-title">{{ $t('producto.filters') }}</span>
    </div>

    <v-card-text class="filters-content pa-0">
      <!-- Depósito Section (solo si el usuario tiene más de un depósito) -->
      <div v-if="showDepositoFilter" class="filter-section">
        <div class="section-header">
          <v-icon small color="primary" class="section-icon">mdi-warehouse</v-icon>
          <span class="section-title-text">{{ $t('producto.filterSection.deposito') }}</span>
        </div>
        <div class="section-content">
          <v-select
            v-model="selectedDepositos"
            :items="depositosItems"
            item-text="nombre"
            item-value="id"
            :label="$t('producto.labels.deposito')"
            prepend-inner-icon="mdi-warehouse"
            color="secondary"
            dense
            outlined
            multiple
            chips
            deletable-chips
            hide-details
            class="filter-field"
            @change="onDepositoFilterChange"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip
                v-if="index === 0"
                small
                color="secondary"
                text-color="white"
                class="mr-2"
              >
                {{ getDepositoSelectionLabel(item) }}
                <span v-if="selectedDepositos.length > 1" class="ml-1">
                  (+{{ selectedDepositos.length - 1 }})
                </span>
              </v-chip>
            </template>
          </v-select>
        </div>
      </div>

      <!-- Identificación Section -->
      <div class="filter-section">
        <div class="section-header">
          <v-icon small color="primary" class="section-icon">mdi-card-text</v-icon>
          <span class="section-title-text">{{ $t('producto.filterSection.identification') }}</span>
        </div>
        
        <div class="section-content">
          <v-text-field
            v-model="filters[0].value"
            :label="$t('producto.labels.nombre')"
            prepend-inner-icon="mdi-tag-text"
            color="secondary"
            dense
            outlined
            hide-details
            class="filter-field mb-3"
            @keyup.enter="setFilters"
          />

          <v-text-field
            v-model="filters[1].value"
            :label="$t('producto.labels.marca')"
            prepend-inner-icon="mdi-tag"
            color="secondary"
            dense
            outlined
            hide-details
            class="filter-field mb-3"
            @keyup.enter="setFilters"
          />
          
          <v-text-field
            v-model="filters[2].value"
            :label="$t('producto.labels.modelo')"
            prepend-inner-icon="mdi-shape"
            color="secondary"
            dense
            outlined
            hide-details
            class="filter-field mb-3"
            @keyup.enter="setFilters"
          />
          
          <v-text-field
            v-model="filters[3].value"
            :label="$t('producto.labels.tamanio')"
            prepend-inner-icon="mdi-ruler"
            color="secondary"
            dense
            outlined
            hide-details
            class="filter-field mb-3"
            @keyup.enter="setFilters"
          />
          
          <v-text-field
            v-model="filters[4].value"
            :label="$t('producto.labels.codigoBarras')"
            prepend-inner-icon="mdi-barcode"
            color="secondary"
            dense
            outlined
            hide-details
            class="filter-field"
            @keyup.enter="setFilters"
          />
        </div>


      </div>
            <!-- Proveedor Section -->
            <div class="filter-section">
        <div class="section-header">
          <v-icon small color="primary" class="section-icon">mdi-truck-delivery</v-icon>
          <span class="section-title-text">{{ $t('producto.filterSection.proveedor') }}</span>
        </div>
        <div class="section-content">
          <v-autocomplete
            v-model="proveedorFilterId"
            :items="proveedoresItems"
            item-text="nombre"
            item-value="id"
            :label="$t('producto.labels.proveedor')"
            prepend-inner-icon="mdi-account-tie"
            color="secondary"
            dense
            outlined
            hide-details
            clearable
            class="filter-field"
            @input="onProveedorFilterInput"
          />
        </div>
      </div>

      <!-- Valores Section -->
      <div class="filter-section">
        <div class="section-header">
          <v-icon small color="primary" class="section-icon">mdi-numeric</v-icon>
          <span class="section-title-text">{{ $t('producto.filterSection.numeric') }}</span>
        </div>
        
        <div class="section-content">
          <!-- Stock Filter -->
          <div class="numeric-filter-group mb-4">
            <div class="numeric-filter-label mb-2">
              <v-icon small color="secondary" class="mr-2">mdi-package-variant</v-icon>
              <span>{{ $t('producto.labels.stock') }}</span>
            </div>
            <v-select
              v-model="filters[7].operator"
              :items="operadoresNumericos"
              :label="$t('producto.operators.label')"
              color="secondary"
              dense
              outlined
              hide-details
              class="filter-field mb-2"
            />
            <v-text-field
              v-model.number="filters[7].value"
              :label="$t('producto.labels.value')"
              color="secondary"
              type="number"
              min="0"
              dense
              outlined
              hide-details
              class="filter-field"
              @keyup.enter="setFilters"
            />
          </div>

          <!-- Precio Venta Filter -->
          <div class="numeric-filter-group">
            <div class="numeric-filter-label mb-2">
              <v-icon small color="secondary" class="mr-2">mdi-currency-usd</v-icon>
              <span>{{ $t('producto.labels.precioVenta') }}</span>
            </div>
            <v-select
              v-model="filters[8].operator"
              :items="operadoresNumericos"
              :label="$t('producto.operators.label')"
              color="secondary"
              dense
              outlined
              hide-details
              class="filter-field mb-2"
            />
            <v-text-field
              v-model.number="filters[8].value"
              :label="$t('producto.labels.value')"
              color="secondary"
              type="number"
              min="0"
              step="0.01"
              dense
              outlined
              hide-details
              class="filter-field"
              @keyup.enter="setFilters"
            />
          </div>
        </div>
      </div>

      <!-- Estado Section -->
      <div class="filter-section">
        <div class="section-header">
          <v-icon small color="primary" class="section-icon">mdi-checkbox-marked-circle</v-icon>
          <span class="section-title-text">{{ $t('producto.filterSection.state') }}</span>
        </div>
        
        <div class="section-content">
          <div class="switch-container mb-3">
            <v-switch
              v-model="filters[5].value"
              :true-value="'true'"
              :false-value="null"
              color="secondary"
              hide-details
              dense
              class="custom-switch"
              @change="setFilters"
            >
              <template v-slot:label>
                <div class="switch-label-wrapper">
                  <v-icon small color="warning" class="mr-2">mdi-alert</v-icon>
                  <span>{{ $t('producto.labels.stockAlerta') }}</span>
                </div>
              </template>
            </v-switch>
          </div>
          
          <div class="switch-container">
            <v-switch
              v-model="filters[6].value"
              :true-value="'true'"
              :false-value="null"
              color="secondary"
              hide-details
              dense
              class="custom-switch"
              @change="setFilters"
            >
              <template v-slot:label>
                <div class="switch-label-wrapper">
                  <v-icon small color="error" class="mr-2">mdi-calendar-alert</v-icon>
                  <span>{{ $t('producto.labels.productosVencidos') }}</span>
                </div>
              </template>
            </v-switch>
          </div>
        </div>
      </div>
    </v-card-text>

    <!-- Action Buttons -->
    <div class="filters-actions">
      <v-btn
        text
        small
        color="grey darken-1"
        class="action-btn mb-2"
        @click="cleanFilters()"
      >
        <v-icon small left>mdi-filter-remove</v-icon>
        {{ $t('producto.actions.clearFilters') }}
      </v-btn>
      
      <v-btn
        depressed
        small
        color="secondary"
        class="action-btn white--text"
        @click="setFilters()"
      >
        <v-icon small left>mdi-check</v-icon>
        {{ $t('producto.actions.applyFilters') }}
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import ProveedorProvider from "../../../../proveedor/providers/ProveedorProvider";

export default {
  name: "ProductoFilters",
  components: {},
  props: {
    value: Array,
    showDepositoFilter: {
      type: Boolean,
      default: false,
    },
    userDepositos: {
      type: Array,
      default: () => [],
    },
    depositoIdSeleccionado: {
      type: String,
      default: null,
    },
  },
  computed: {
    filters: {
      get() { return this.value },
      set(val) {
        this.$emit('input', val)
      }
    },
    depositosItems() {
      return (this.userDepositos || []).map(d => ({
        id: String(d.id),
        nombre: d.nombre || '',
      }));
    },
    proveedoresItems() {
      return (this.proveedores || []).map((p) => ({
        id: String(p.id),
        nombre: p.nombre || '',
      }));
    },
    proveedorFilterId: {
      get() {
        const f = (this.value || []).find((x) => x.field === 'proveedorId');
        return f && f.value ? String(f.value) : null;
      },
      set(v) {
        const f = (this.value || []).find((x) => x.field === 'proveedorId');
        if (f) f.value = v || null;
      },
    },
  },
  data() {
    return {
      proveedores: [],
      selectedDepositos: [],
      operadoresNumericos: [
        { text: this.$t("producto.operators.gte"), value: "$gte" },
        { text: this.$t("producto.operators.lte"), value: "$lte" },
        { text: this.$t("producto.operators.eq"), value: "$eq" }
      ]
    }
  },
  mounted() {
    this.loadProveedores();
  },
  watch: {
    value: { handler: 'syncSelectedDepositosFromFilters', immediate: true },
    showDepositoFilter: 'syncSelectedDepositosFromFilters',
    userDepositos: { handler: 'syncSelectedDepositosFromFilters', deep: true },
  },
  methods: {
    async loadProveedores() {
      try {
        const r = await ProveedorProvider.fetchProveedores();
        this.proveedores = (r.data && r.data.proveedorFetch) || [];
      } catch (e) {
        console.error(e);
        this.proveedores = [];
      }
    },
    /** Aplica listado al elegir o limpiar proveedor (valor ya actualizado en el filtro vía v-model). */
    onProveedorFilterInput() {
      this.$nextTick(() => this.setFilters());
    },
    syncSelectedDepositosFromFilters() {
      if (!this.showDepositoFilter) return;
      const filters = this.value || [];
      const depositoFilter = filters.find(f => f.field === 'deposito' || f.field === 'depositoId');
      if (depositoFilter && depositoFilter.value) {
        const valueStr = String(depositoFilter.value);
        if (depositoFilter.operator === '$in') {
          const ids = depositoFilter.value.split(',').map(id => id.trim()).filter(Boolean);
          this.selectedDepositos = ids.map(id => String(id));
        } else {
          this.selectedDepositos = [valueStr];
        }
      } else if (this.depositoIdSeleccionado) {
        this.selectedDepositos = [String(this.depositoIdSeleccionado)];
      } else {
        this.selectedDepositos = [];
      }
    },
    getDepositoSelectionLabel(item) {
      if (!item) return '';
      const nombre = typeof item === 'object' ? item.nombre : null;
      const id = typeof item === 'object' ? item.id : item;
      return nombre || id || '';
    },
    onDepositoFilterChange(selection) {
      if (!selection || !Array.isArray(selection)) return;
      const sel = selection.map(id => String(id));

      const depositoIds = (this.userDepositos || []).map(d => String(d.id));
      let depositoFilter = this.filters.find(f => f.field === 'deposito' || f.field === 'depositoId');
      if (!depositoFilter) {
        depositoFilter = { field: 'deposito', operator: '$eq', value: null };
        this.filters.push(depositoFilter);
      }

      if (sel.length === 0) {
        depositoFilter.value = this.depositoIdSeleccionado || depositoIds[0] || null;
        depositoFilter.operator = '$eq';
        this.selectedDepositos = depositoFilter.value ? [depositoFilter.value] : [];
      } else {
        this.selectedDepositos = sel;
        if (sel.length === 1) {
          depositoFilter.operator = '$eq';
          depositoFilter.value = sel[0];
        } else {
          depositoFilter.operator = '$in';
          depositoFilter.value = sel.join(',');
        }
      }
      this.$emit("updateFilters", this.filters);
    },
    setFilters() {
      if (this.filters[7] && this.filters[7].value && this.filters[7].value < 0) {
        this.filters[7].value = 0;
      }
      if (this.filters[8] && this.filters[8].value && this.filters[8].value < 0) {
        this.filters[8].value = 0;
      }
      this.$emit("updateFilters", this.filters);
    },
    cleanFilters() {
      this.$emit("clearFilter", this.filters);
    },
  },
};
</script>

<style scoped>
.filters-sidebar {
  height: calc(100vh - 100px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 20px;
  border-radius: 12px !important;
  background: #ffffff;
}

/* Header */
.filters-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 12px 12px 0 0;
}

.filters-title {
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

/* Content */
.filters-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

.filter-section {
  padding: 0;
  margin-bottom: 4px;
}

.section-header {
  background: #f5f7fa;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 2px solid #e1e8ed;
}

.section-icon {
  opacity: 0.9;
}

.section-title-text {
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #2c3e50;
}

.section-content {
  padding: 16px;
  background: #ffffff;
}

/* Filter Fields */
.filter-field {
  font-size: 0.875rem;
}

.filter-field >>> .v-input__slot {
  min-height: 38px !important;
  background: #fafbfc !important;
  transition: all 0.2s ease;
}

.filter-field >>> .v-input__slot:hover {
  background: #f0f3f7 !important;
}

.filter-field >>> fieldset {
  border-color: #e1e8ed !important;
  border-width: 1px !important;
}

.filter-field >>> .v-input__slot:hover fieldset {
  border-color: #cbd5e0 !important;
}

.filter-field.v-input--is-focused >>> fieldset {
  border-width: 2px !important;
}

/* Numeric Filters */
.numeric-filter-group {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.numeric-filter-label {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 500;
  color: #495057;
}

/* Switches */
.switch-container {
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;
}

.switch-container:hover {
  background: #e9ecef;
  border-color: #dee2e6;
}

.custom-switch {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.custom-switch >>> .v-input--selection-controls__input {
  margin-right: 8px;
}

.switch-label-wrapper {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: #495057;
  font-weight: 500;
}

/* Action Buttons */
.filters-actions {
  padding: 16px;
  border-top: 1px solid #e1e8ed;
  background: #fafbfc;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.action-btn {
  width: 100%;
  height: 38px !important;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.5px;
  border-radius: 8px !important;
  transition: all 0.2s ease;
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Scrollbar styling */
.filters-content::-webkit-scrollbar {
  width: 8px;
}

.filters-content::-webkit-scrollbar-track {
  background: #f1f3f5;
  border-radius: 10px;
  margin: 8px 0;
}

.filters-content::-webkit-scrollbar-thumb {
  background: #9ca3af;
  border-radius: 10px;
  transition: all 0.2s ease;
}

.filters-content::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

/* Responsive */
@media (max-width: 960px) {
  .filters-sidebar {
    height: auto;
    position: relative;
    top: 0;
    margin-bottom: 20px;
  }
  
  .filters-content {
    max-height: 60vh;
  }
}
</style>
